import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as EmptyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { apiClient } from '@/services/api';
import ProjectReportEditor from './ProjectReportEditor';

interface Chapter {
  id: number;
  title: string;
  content?: string;
  level: number;
  sort_order: number;
  status: string;
  parent_id?: number;
  children?: Chapter[];
  expanded?: boolean;
}

interface ProjectReportWorkspaceProps {
  projectId: string;
}

// 项目自评报告默认模板章节
const DEFAULT_TEMPLATE_CHAPTERS = [
  {
    title: '一、项目基本情况',
    level: 1,
    children: [
      {
        title: '（一）项目基本信息',
        level: 2,
        children: [
          { title: '1、项目背景', level: 3 },
          { title: '2、项目实施内容', level: 3 }
        ]
      },
      {
        title: '（二）项目支出情况',
        level: 2,
        children: [
          { title: '1、项目预算情况', level: 3 },
          { title: '2、项目预算执行情况', level: 3 }
        ]
      }
    ]
  },
  {
    title: '二、绩效自评工作开展情况',
    level: 1,
    children: [
      { title: '（一）绩效自评价目的和意义', level: 2 },
      { title: '（二）绩效自评价对象和范围', level: 2 },
      { title: '（三）绩效评价的依据', level: 2 },
      { title: '（四）评分方法', level: 2 },
      { title: '（五）绩效自评工作组织', level: 2 }
    ]
  },
  {
    title: '三、绩效自评结果及分析',
    level: 1,
    children: [
      { title: '（一）绩效自评价结果', level: 2 },
      {
        title: '（二）自评指标分析',
        level: 2,
        children: [
          { title: '1、预算执行指标分析', level: 3 },
          { title: '2、产出指标分析', level: 3 },
          { title: '3、效益指标分析', level: 3 },
          { title: '4、满意度指标分析', level: 3 }
        ]
      }
    ]
  },
  {
    title: '四、成果和问题',
    level: 1,
    children: [
      { title: '（一）项目实施的成果', level: 2 },
      { title: '（二）项目存在的问题', level: 2 }
    ]
  },
  {
    title: '五、建议',
    level: 1,
    children: [
      { title: '（一）强化预算绩效管理意识，提升项目产出效益', level: 2 },
      { title: '（二）加强学习培训，进一步提高绩效管理水平', level: 2 },
      { title: '（三）分析原因，提高施工效率', level: 2 }
    ]
  },
  { title: '六、其他需要说明的问题', level: 1 },
  {
    title: '七、附件',
    level: 1,
    children: [
      { title: '附件一：项目绩效自评表', level: 2 }
    ]
  }
];

const ProjectReportWorkspace: React.FC<ProjectReportWorkspaceProps> = ({ projectId }) => {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  // 章节管理状态
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState<Chapter | null>(null);
  const [chapterTitle, setChapterTitle] = useState('');
  const [hoveredChapterId, setHoveredChapterId] = useState<number | null>(null);
  const [newChapterParentId, setNewChapterParentId] = useState<number | ''>('');
  const [newChapterLevel, setNewChapterLevel] = useState(1);

  // AI生成用户提示词弹框
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 添加expanded属性到章节
  const addExpanded = (chapterList: Chapter[]): Chapter[] => {
    return chapterList.map(ch => ({
      ...ch,
      expanded: true,
      children: ch.children ? addExpanded(ch.children) : []
    }));
  };

  // 递归查找第一个叶子节点
  const findFirstLeafChapter = (chapterList: Chapter[]): Chapter | null => {
    for (const chapter of chapterList) {
      if (!chapter.children || chapter.children.length === 0) {
        return chapter;
      }
      const found = findFirstLeafChapter(chapter.children);
      if (found) return found;
    }
    return null;
  };

  // 加载章节列表
  const loadChapters = useCallback(async (autoInit = true) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/projects/${projectId}/project-self-evaluation-report`);

      if (!response.success) {
        throw new Error(response.message || '获取报告章节失败');
      }

      const chaptersData = response.data || [];

      // 添加expanded属性
      const processedChapters = addExpanded(chaptersData);
      setChapters(processedChapters);

      // 如果没有章节,自动初始化
      if (chaptersData.length === 0 && autoInit) {
        await initializeTemplate();
        return;
      }

      // 如果有章节但没有选中,选中第一个叶子节点
      if (chaptersData.length > 0) {
        const firstLeaf = findFirstLeafChapter(processedChapters);
        if (firstLeaf) {
          setSelectedChapter(firstLeaf);
        }
      }
    } catch (error: any) {
      showSnackbar('加载报告章节失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 初始化模板
  const initializeTemplate = async () => {
    try {
      const response = await apiClient.post(`/projects/${projectId}/project-self-evaluation-report/initialize`);

      if (!response.success) {
        throw new Error(response.message || '初始化模板失败');
      }

      // 重新加载章节
      await loadChapters(false);
      showSnackbar('报告模板初始化成功', 'success');
    } catch (error) {
      showSnackbar('初始化报告模板失败', 'error');
    }
  };

  useEffect(() => {
    loadChapters();
  }, [loadChapters]);

  // 保存章节内容
  const handleSave = async (content: string): Promise<boolean> => {
    if (!selectedChapter) return false;

    try {
      setSaving(true);
      const response = await apiClient.put(
        `/projects/${projectId}/project-self-evaluation-report/${selectedChapter.id}`,
        { content, status: 'completed' }
      );

      if (!response.success) {
        throw new Error(response.message || '保存失败');
      }

      // 更新本地状态
      const updateChapterStatus = (chapterList: Chapter[]): Chapter[] => {
        return chapterList.map(ch => {
          if (ch.id === selectedChapter.id) {
            return { ...ch, content, status: 'completed' };
          }
          if (ch.children) {
            return { ...ch, children: updateChapterStatus(ch.children) };
          }
          return ch;
        });
      };

      setChapters(updateChapterStatus(chapters));
      showSnackbar('保存成功', 'success');
      return true;
    } catch (error) {
      showSnackbar('保存失败', 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // 切换展开状态
  const toggleExpand = (chapterId: number) => {
    const toggle = (list: Chapter[]): Chapter[] => {
      return list.map(ch => {
        if (ch.id === chapterId) {
          return { ...ch, expanded: !ch.expanded };
        }
        if (ch.children) {
          return { ...ch, children: toggle(ch.children) };
        }
        return ch;
      });
    };
    setChapters(toggle(chapters));
  };

  // 获取进度
  const getProgress = () => {
    let total = 0;
    let completed = 0;

    const countChapters = (list: Chapter[]) => {
      list.forEach(ch => {
        total++;
        if (ch.status === 'completed') completed++;
        if (ch.children) countChapters(ch.children);
      });
    };

    countChapters(chapters);
    return { total, completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  // AI生成当前章节 - 打开提示词输入弹框
  const handleGenerateClick = () => {
    if (!selectedChapter) return;
    setUserPrompt('');
    setPromptDialogOpen(true);
  };

  // AI生成当前章节 - 确认生成
  const handleGenerateChapter = async () => {
    if (!selectedChapter) return;

    try {
      setPromptDialogOpen(false);
      setGenerating(true);
      showSnackbar('正在AI生成章节内容...', 'info');

      const response = await apiClient.post(
        `/projects/${projectId}/project-self-evaluation-report/${selectedChapter.id}/generate`,
        { user_prompt: userPrompt }
      );

      if (!response.success) {
        throw new Error(response.message || '生成失败');
      }

      // 更新章节内容
      if (response.data) {
        const updateChapterContent = (chapterList: Chapter[]): Chapter[] => {
          return chapterList.map(ch => {
            if (ch.id === selectedChapter.id) {
              return { ...ch, content: response.data.content, status: 'completed' };
            }
            if (ch.children) {
              return { ...ch, children: updateChapterContent(ch.children) };
            }
            return ch;
          });
        };

        setChapters(updateChapterContent(chapters));
        setSelectedChapter({ ...selectedChapter, content: response.data.content, status: 'completed' });
      }

      showSnackbar('章节内容生成成功', 'success');
    } catch (error) {
      showSnackbar('生成失败', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // 清空所有章节
  const handleClearAll = async () => {
    setClearDialogOpen(false);
    try {
      setLoading(true);
      const response = await apiClient.delete(`/projects/${projectId}/project-self-evaluation-report/clear/all`);

      if (!response.success) {
        throw new Error(response.message || '清空章节失败');
      }

      await loadChapters(false);
      showSnackbar('章节已清空', 'success');
    } catch (error) {
      showSnackbar('清空失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 导出Word
  const handleExportWord = async () => {
    try {
      showSnackbar('正在生成Word文档...', 'info');
      // 直接下载文件
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const response = await fetch(`/api/v1/projects/${projectId}/project-self-evaluation-report/export-word`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || '导出失败');
      }

      // 从响应头获取文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = '项目自评报告.docx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // 下载文件
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showSnackbar('Word导出成功', 'success');
    } catch (error: any) {
      showSnackbar(error.message || '导出失败', 'error');
    }
  };

  const handleExportMarkdown = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/project-self-evaluation-report/export/markdown`);
      if (response.success && response.data?.content) {
        const blob = new Blob([response.data.content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = '项目自评报告.md';
        a.click();
        URL.revokeObjectURL(url);
        showSnackbar('Markdown导出成功', 'success');
      }
    } catch (error) {
      showSnackbar('导出失败', 'error');
    }
  };

  // 章节管理函数
  const handleAddChapter = () => {
    setEditingChapter(null);
    setChapterTitle('');
    setNewChapterParentId('');
    setNewChapterLevel(1);
    setAddDialogOpen(true);
  };

  const handleEditChapter = (chapter: Chapter, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingChapter(chapter);
    setChapterTitle(chapter.title);
    setEditDialogOpen(true);
  };

  const handleDeleteChapterClick = (chapter: Chapter, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingChapter(chapter);
    setDeleteDialogOpen(true);
  };

  const handleSaveChapterTitle = async () => {
    if (!chapterTitle.trim()) {
      showSnackbar('章节标题不能为空', 'error');
      return;
    }

    try {
      if (editingChapter) {
        // 编辑章节
        const response = await apiClient.put(
          `/projects/${projectId}/project-self-evaluation-report/${editingChapter.id}`,
          { title: chapterTitle }
        );
        if (response.success) {
          showSnackbar('章节标题更新成功', 'success');
          setEditDialogOpen(false);
          await loadChapters(false);
        }
      } else {
        // 添加章节 - 计算sort_order
        let sortOrder = 0;
        if (newChapterParentId) {
          // 如果有父章节，找到父章节下子章节的数量
          const findChildrenCount = (chapterList: Chapter[], parentId: number): number => {
            for (const ch of chapterList) {
              if (ch.id === parentId && ch.children) {
                return ch.children.length;
              }
              if (ch.children) {
                const count = findChildrenCount(ch.children, parentId);
                if (count >= 0) return count;
              }
            }
            return 0;
          };
          sortOrder = findChildrenCount(chapters, newChapterParentId as number);
        } else {
          // 作为一级章节，使用当前一级章节数量
          sortOrder = chapters.length;
        }

        const response = await apiClient.post(
          `/projects/${projectId}/project-self-evaluation-report`,
          {
            title: chapterTitle,
            level: newChapterLevel,
            parent_id: newChapterParentId || null,
            sort_order: sortOrder
          }
        );
        if (response.success) {
          showSnackbar('章节添加成功', 'success');
          setAddDialogOpen(false);
          await loadChapters(false);
        }
      }
    } catch (error) {
      showSnackbar('操作失败', 'error');
    }
  };

  const handleDeleteChapter = async () => {
    if (!editingChapter) return;

    try {
      const response = await apiClient.delete(
        `/projects/${projectId}/project-self-evaluation-report/${editingChapter.id}`
      );
      if (response.success) {
        showSnackbar('章节删除成功', 'success');
        setDeleteDialogOpen(false);
        if (selectedChapter?.id === editingChapter.id) {
          setSelectedChapter(null);
        }
        await loadChapters(false);
      }
    } catch (error) {
      showSnackbar('删除失败', 'error');
    }
  };

  // 渲染章节项
  const renderChapterItem = (chapter: Chapter, depth = 0) => {
    const hasChildren = chapter.children && chapter.children.length > 0;
    const isSelected = selectedChapter?.id === chapter.id;
    const isHovered = hoveredChapterId === chapter.id;
    const statusIcon = chapter.status === 'completed'
      ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
      : <EmptyIcon sx={{ fontSize: 16, color: 'grey.400' }} />;

    return (
      <Box key={chapter.id}>
        <Box
          onClick={() => !hasChildren && setSelectedChapter(chapter)}
          onMouseEnter={() => setHoveredChapterId(chapter.id)}
          onMouseLeave={() => setHoveredChapterId(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            py: 0.75,
            px: 1 + depth * 1.5,
            cursor: hasChildren ? 'default' : 'pointer',
            bgcolor: isSelected ? 'primary.100' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': {
              bgcolor: isSelected ? 'primary.200' : 'grey.100'
            },
            borderLeft: isSelected ? '3px solid' : '3px solid transparent',
            borderColor: isSelected ? 'primary.main' : 'transparent'
          }}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(chapter.id);
              }}
              sx={{ p: 0.25 }}
            >
              {chapter.expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          )}
          {!hasChildren && <Box sx={{ width: 20 }} />}
          {statusIcon}
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              fontSize: depth === 0 ? '0.875rem' : '0.8125rem',
              fontWeight: depth === 0 ? 500 : 400,
              color: hasChildren ? 'text.primary' : 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {chapter.title}
          </Typography>
          {/* 操作按钮 */}
          {isHovered && (
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              <Tooltip title="编辑标题">
                <IconButton
                  size="small"
                  onClick={(e) => handleEditChapter(chapter, e)}
                  sx={{ p: 0.25 }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="删除章节">
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteChapterClick(chapter, e)}
                  sx={{ p: 0.25, color: 'error.main' }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        {hasChildren && chapter.expanded && (
          <Box>
            {chapter.children!.map(child => renderChapterItem(child, depth + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const progress = getProgress();

  if (loading && chapters.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>加载中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
      {/* 主体布局 */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {chapters.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="h6" color="text.secondary">
              暂无报告内容
            </Typography>
            <Typography variant="body2" color="text.secondary">
              正在初始化报告模板...
            </Typography>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* 左侧章节列表 */}
            <Box
              sx={{
                width: 280,
                bgcolor: 'white',
                borderRight: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              {/* 章节列表标题 */}
              <Box sx={{
                p: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  章节目录
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddChapter}
                    sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                  >
                    添加章节
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ExportIcon />}
                    onClick={handleExportWord}
                    sx={{ minWidth: 'auto', fontSize: '0.75rem' }}
                  >
                    导出报告
                  </Button>
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
                {chapters.map(ch => renderChapterItem(ch))}
              </Box>
            </Box>

            {/* 右侧编辑区 */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* 当前章节标题和操作 */}
              {selectedChapter && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'white',
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {selectedChapter.title}
                  </Typography>
                </Box>
              )}

              {/* 编辑器 */}
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <ProjectReportEditor
                  chapter={selectedChapter}
                  onSave={handleSave}
                  saving={saving}
                  onGenerate={handleGenerateClick}
                  generating={generating}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* 清空确认对话框 */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
        <DialogTitle>确认清空</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要清空所有章节内容吗？此操作不可恢复。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>取消</Button>
          <Button onClick={handleClearAll} color="error">确认清空</Button>
        </DialogActions>
      </Dialog>

      {/* 编辑章节标题对话框 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>编辑章节标题</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="章节标题"
            fullWidth
            value={chapterTitle}
            onChange={(e) => setChapterTitle(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSaveChapterTitle()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>取消</Button>
          <Button onClick={handleSaveChapterTitle} variant="contained">保存</Button>
        </DialogActions>
      </Dialog>

      {/* 添加章节对话框 */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>添加新章节</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="章节标题"
            fullWidth
            value={chapterTitle}
            onChange={(e) => setChapterTitle(e.target.value)}
            placeholder="请输入章节标题"
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="上级章节"
            fullWidth
            value={newChapterParentId}
            onChange={(e) => {
              const val = e.target.value;
              setNewChapterParentId(val);
              // 根据父章节自动设置级别
              if (val === '') {
                setNewChapterLevel(1);
              } else {
                // 找到父章节的级别，子章节级别 = 父章节级别 + 1
                const findLevel = (chapterList: Chapter[], parentId: number, currentLevel = 1): number => {
                  for (const ch of chapterList) {
                    if (ch.id === parentId) return currentLevel + 1;
                    if (ch.children) {
                      const level = findLevel(ch.children, parentId, currentLevel + 1);
                      if (level > 0) return level;
                    }
                  }
                  return -1;
                };
                const level = findLevel(chapters, val as number);
                if (level > 0) setNewChapterLevel(level);
              }
            }}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">作为一级章节</MenuItem>
            {(() => {
              const flatChapters: { id: number; title: string; level: number }[] = [];
              const flattenList = (chapterList: Chapter[], level = 1) => {
                chapterList.forEach(ch => {
                  flatChapters.push({ id: ch.id, title: ch.title, level });
                  if (ch.children) flattenList(ch.children, level + 1);
                });
              };
              flattenList(chapters);
              return flatChapters.map(ch => (
                <MenuItem key={ch.id} value={ch.id}>
                  {'  '.repeat(ch.level - 1)}{ch.title}
                </MenuItem>
              ));
            })()}
          </TextField>
          <TextField
            select
            margin="dense"
            label="章节级别"
            fullWidth
            value={newChapterLevel}
            onChange={(e) => setNewChapterLevel(Number(e.target.value))}
            helperText="通常根据上级章节自动设置"
          >
            <MenuItem value={1}>一级章节</MenuItem>
            <MenuItem value={2}>二级章节</MenuItem>
            <MenuItem value={3}>三级章节</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>取消</Button>
          <Button onClick={handleSaveChapterTitle} variant="contained">添加</Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除章节"{editingChapter?.title}"吗？
            {editingChapter?.children && editingChapter.children.length > 0 && (
              <Typography component="span" sx={{ display: 'block', mt: 1, color: 'error.main' }}>
                注意：该章节下的所有子章节也会被删除！
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteChapter} color="error" variant="contained">确认删除</Button>
        </DialogActions>
      </Dialog>

      {/* AI生成提示词输入弹窗 */}
      <Dialog open={promptDialogOpen} onClose={() => setPromptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <AutoAwesomeIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">AI生成章节内容</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            可选：输入额外的要求或提示，帮助AI更好地生成"{selectedChapter?.title}"的内容
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="例如：重点关注项目的经济效益分析..."
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleGenerateChapter}
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
          >
            开始生成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 提示消息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectReportWorkspace;
